import logo from '@assets/logo.svg';
import { OnlineUser } from '@components/features/organisms/SideBar//OnlineUser';
import { Footer } from '@components/features/organisms/SideBar/Footer';
import {
  useMenuStructure,
  useSystemMenuStructure,
} from '@components/features/organisms/SideBar/Menu/hooks';
import { MenuItem } from '@components/features/organisms/SideBar/Menu/MenuItem';
import type { SidebarMenuItem } from '@components/features/organisms/SideBar/Menu/types';
import { ProfilePath } from '@components/modules/profile/urls';
import { Box } from '@components/ui/atoms/Box';
import { Flex } from '@components/ui/atoms/Flex';
import { IconButton } from '@components/ui/atoms/IconButton';
import { CloseIcon, Icon } from '@components/ui/atoms/Icons';
import { Image } from '@components/ui/atoms/Image';
import { Stack } from '@components/ui/atoms/Stack';
import { Text } from '@components/ui/atoms/Text';
import { Divider } from '@components/ui/molecules/Divider';
import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router';

interface Props {
  onClose: () => void;
}

export const MobileMenuContent: React.FC<Props> = memo(({ onClose }) => {
  const navigate = useNavigate();
  const menuStructure = useMenuStructure();
  const systemMenuStructure = useSystemMenuStructure();

  const handleNavigateToProfile = useCallback(() => {
    void navigate(ProfilePath);
    onClose();
  }, [navigate, onClose]);

  const handleLogoClick = useCallback(() => {
    void navigate('/');
    onClose();
  }, [navigate, onClose]);

  const renderMenuItem = useCallback(
    (menuItem: SidebarMenuItem) => {
      const hasChildren = menuItem.children && menuItem.children.length > 0;

      return (
        <Box
          key={menuItem.id}
          onClick={(e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            const clickedLink = target.closest('a');

            // If clicked on a link (child item navigation), close the menu
            if (clickedLink) {
              onClose();

              return;
            }

            // If parent has children, let MenuGroup handle the toggle
            // Don't close the menu
            if (hasChildren) {
              return;
            }

            // For items without children and no link, close menu
            onClose();
          }}
          borderRadius="8px"
          transition="all 0.2s"
        >
          <MenuItem menuItem={menuItem} isExpanded={true} />
        </Box>
      );
    },
    [onClose]
  );

  return (
    <Flex
      flexDirection="column"
      height="full"
      minHeight={0}
      width="full"
      flex="1"
      overflow="hidden"
    >
      {/* Close button at top */}
      <Flex
        flexShrink={0}
        alignItems="center"
        justifyContent="space-between"
        p="16px"
        borderBottomWidth="1px"
        borderColor="blackAlpha.100"
      >
        <Flex
          alignItems="center"
          gap="8px"
          cursor="pointer"
          onClick={() => {
            handleLogoClick();
          }}
        >
          <Image src={logo} height="24px" width="28px" />
          <Text fontSize="14px" color="title.dark" fontWeight="700">
            Bhutan Payments
          </Text>
        </Flex>
        <IconButton
          aria-label="Close menu"
          variant="ghost"
          borderRadius="8px"
          _hover={{ bg: 'blackAlpha.50' }}
          onClick={onClose}
        >
          <Icon css={{ width: '20px', height: '20px' }}>
            <CloseIcon />
          </Icon>
        </IconButton>
      </Flex>

      <Stack
        display="flex"
        flexDirection="column"
        gap="24px"
        p="16px"
        pb="16px"
        flex="1"
        minHeight={0}
        overflowY="auto"
        css={{ WebkitOverflowScrolling: 'touch' }}
      >
        {/* Profile Section - Using OnlineUser pattern */}
        <Flex
          display="flex"
          alignItems="center"
          gap="12px"
          px="2"
          py="3"
          cursor="pointer"
          onClick={handleNavigateToProfile}
          borderRadius="8px"
          transition="all 0.2s"
          _hover={{
            bg: 'body.50/5',
          }}
        >
          <OnlineUser />
        </Flex>

        {/* Payment Section - Using Menu items */}
        <Flex flexDirection="column" gap="12px">
          <Box w="full" px="2">
            <Text
              fontSize="12px"
              fontWeight="500"
              textTransform="uppercase"
              color="body.dark"
              pb="2"
            >
              Payment
            </Text>
            <Divider left="0" right="0" width="full" height="1px" opacity="0.5" />
          </Box>
          <Stack display="flex" flexDirection="column" gap="4px">
            {menuStructure.map(renderMenuItem)}
          </Stack>
        </Flex>

        {/* System Section - Using Menu items */}
        <Flex flexDirection="column" gap="12px">
          <Box w="full" px="2">
            <Text
              fontSize="12px"
              fontWeight="500"
              textTransform="uppercase"
              color="body.dark"
              pb="2"
            >
              System
            </Text>
            <Divider left="0" right="0" width="full" height="1px" opacity="0.5" />
          </Box>
          <Stack display="flex" flexDirection="column" gap="4px">
            {systemMenuStructure.map(renderMenuItem)}
          </Stack>
        </Flex>
        <Flex flex="1 1 auto" alignItems="flex-end">
          <Footer isExpanded={true} />
        </Flex>
      </Stack>
    </Flex>
  );
});

MobileMenuContent.displayName = 'MobileMenuContent';
